// B-2: card title + meta row — the body content both card variants share.
//
// The TITLE is the stream's own title and is the primary text; the channel
// name is secondary (the legacy Broadcast.js card has this inverted — do
// not copy it). Renders as a fragment inside StreamCard's body slot, which
// already provides the flex column + 9px gap.
//
// Equal card heights across a row: the title is clamped to 2 lines AND
// given min-height 38px (2 × 14px × 1.35), so 1-line and 2-line titles
// occupy identical vertical space.
//
// All colors come from the browse-* tokens (raw hex fails the designTokens
// jest check).
import React from 'react';

// SOR-625: 26px avatar on mobile (the collapsed one-line meta row leans on
// it), back to 20px inside the desktop card grid.
const AVATAR_CLS =
  'h-[26px] w-[26px] sm:h-5 sm:w-5 shrink-0 rounded-full border border-browse-placeholder-grey-strong';

/**
 * @param {Object} props
 * @param {string} props.title  the stream's own title — primary text
 * @param {?string} props.channelName  secondary; hidden when null
 * @param {?string} props.channelAvatarUrl  null → grey placeholder circle
 * @param {React.ReactNode} [props.metaRight]  right-aligned mono slot —
 *   park code on live cards, local start time on upcoming cards
 */
const StreamCardBody = ({ title, channelName, channelAvatarUrl, metaRight }) => (
  <>
    <h3
      className="min-h-[38px] overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [text-wrap:pretty] font-inter-tight text-sm font-semibold leading-1.35 tracking-card-title text-browse-text-primary-soft"
      title={title}
    >
      {title}
    </h3>
    <div className="flex items-center gap-2">
      {channelAvatarUrl ? (
        <img
          src={channelAvatarUrl}
          alt=""
          className={`${AVATAR_CLS} object-cover`}
        />
      ) : (
        <span
          aria-hidden="true"
          className={`${AVATAR_CLS} bg-browse-placeholder-grey`}
        />
      )}
      {channelName != null && (
        <span className="min-w-0 truncate font-inter-tight text-xs font-medium leading-none text-browse-text-secondary-soft">
          {channelName}
        </span>
      )}
      {/* SOR-625 mobile: meta collapses to one "Channel · Park" string —
          the separator dot only exists below sm, where ml-auto is off */}
      {channelName != null && metaRight != null && (
        <span
          aria-hidden="true"
          className="shrink-0 text-browse-text-tertiary-soft sm:hidden"
        >
          ·
        </span>
      )}
      {metaRight != null && (
        <span className="sm:ml-auto shrink-0 whitespace-nowrap font-plex-mono text-11 font-normal leading-none text-browse-text-tertiary-soft">
          {metaRight}
        </span>
      )}
    </div>
  </>
);

export default StreamCardBody;
